import React from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useFragment } from 'react-relay-offline'
import { last } from 'ramda'
import { csx } from '@vtex/admin-ui'

import type { Occurrence } from '../../shared/types'
import { ModalContentTemplate } from '../../shared/types'
import {
  SalesAssociateMovementsTrackingModalContent,
  SalesAssociateMovementsTrackingModalFooter,
  SalesAssociateMovementsTrackingModalHeader,
  SalesAssociateMovementsTrackingModalItem,
} from '../SalesAssociateMovementsTrackingModal'
import { OccurrencesBreadcrumb } from './OccurrencesBreadcrumb'
import { OccurrencesModalTitle } from './OccurrencesModalTitle'
import type { OccurrencesContentTemplate_salesAssociate$key } from './__generated__/OccurrencesContentTemplate_salesAssociate.graphql'
import { MultipleAssistancesCounter } from './MultipleAssistancesCounter'
import { useOccurrencesModal } from '../../hooks/useOccurrencesModal'

// TODO: https://vtex-dev.atlassian.net/browse/PSM-1670
// The code below has a hotfix for the content overflow problem
// related on link above. The CARD has more explanations about the problem.
const modalPaddingHeight = '64px'
const modalHeaderHeight = '187px'
const modalFooterHeight = '92px'
const modalContentMaxHeight = `calc(100vh - calc(${modalPaddingHeight} + ${modalHeaderHeight} + ${modalFooterHeight}))`

export type OccurrencesContentTemplateProps = {
  salesAssociateRef: OccurrencesContentTemplate_salesAssociate$key
}

export const OccurrencesContentTemplate = ({
  salesAssociateRef,
}: OccurrencesContentTemplateProps) => {
  const {
    selectedOccurrences,
    setSelectedOccurrences,
    setBreadcrumbOccurrences,
    setDisplayedOccurrences,
    displayedOccurences,
    multipleAssistancesData,
    breadcrumbOccurrences,
    pendingConfirmOccurrence,
    setModalContentTemplate,
    setMultipleAssistancesData,
    setPendingConfirmOccurrence,
    options,
    close,
  } = useOccurrencesModal()

  const salesAssociateData = useFragment<OccurrencesContentTemplate_salesAssociate$key>(
    graphql`
      fragment OccurrencesContentTemplate_salesAssociate on SalesAssociate {
        name
        avatar {
          url
        }
      }
    `,
    salesAssociateRef
  )

  const handleMultipleAssistancesChangeStep = () => {
    if (!pendingConfirmOccurrence) {
      return
    }

    setMultipleAssistancesData((prev) => ({
      ...prev,
      currentAssistance: prev.currentAssistance + 1,
    }))

    setSelectedOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      pendingConfirmOccurrence,
    ])

    setDisplayedOccurrences(multipleAssistancesData.occurrences)

    setBreadcrumbOccurrences([])
  }

  const handleOccurrencesConfirmClick = () => {
    const hasMoreAssistances =
      multipleAssistancesData.active &&
      multipleAssistancesData.currentAssistance <
        multipleAssistancesData.assistancesCount

    if (hasMoreAssistances) {
      handleMultipleAssistancesChangeStep()

      return
    }

    if (pendingConfirmOccurrence) {
      options.onConfirm([...selectedOccurrences, pendingConfirmOccurrence])

      close()
    }
  }

  const handleMultipleReferenceReferenceClick = (
    clickedOccurrence: Occurrence
  ) => {
    setModalContentTemplate(ModalContentTemplate.MultipleAssistances)

    setSelectedOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOccurrence,
    ])

    setBreadcrumbOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOccurrence,
    ])

    setMultipleAssistancesData((prev) => ({
      ...prev,
      active: true,
      occurrences: clickedOccurrence.children[0].children,
      currentStep: 1,
    }))
  }

  const handleSalesAssociateReferenceClick = (
    clickedOccurrence: Occurrence
  ) => {
    setModalContentTemplate(ModalContentTemplate.SalesAssociates)
    setDisplayedOccurrences(clickedOccurrence.children[0].children)

    setSelectedOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOccurrence,
    ])

    setBreadcrumbOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOccurrence,
    ])
  }

  const handleOccurrenceWithChildrenClick = (clickedOcurrence: Occurrence) => {
    setSelectedOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOcurrence,
    ])

    setBreadcrumbOccurrences((prevOccurrences) => [
      ...prevOccurrences,
      clickedOcurrence,
    ])

    setDisplayedOccurrences(clickedOcurrence.children)
  }

  const handleOccurrenceClick = (clickedOcurrence: Occurrence) => () => {
    if (pendingConfirmOccurrence?.record.id === clickedOcurrence.record.id) {
      return handleOccurrencesConfirmClick()
    }

    if (clickedOcurrence?.children[0]?.record.reference === 'MULTIPLE') {
      return handleMultipleReferenceReferenceClick(clickedOcurrence)
    }

    if (clickedOcurrence?.children[0]?.record.reference === 'SALES_ASSOCIATE') {
      return handleSalesAssociateReferenceClick(clickedOcurrence)
    }

    if (options.cutInLine) {
      return setPendingConfirmOccurrence(clickedOcurrence)
    }

    if (clickedOcurrence.children?.length) {
      return handleOccurrenceWithChildrenClick(clickedOcurrence)
    }

    setPendingConfirmOccurrence(clickedOcurrence)
  }

  const handleBackClick = () => {
    const prevSelectedOccurrences = selectedOccurrences.slice(0, -1)

    setSelectedOccurrences(prevSelectedOccurrences)

    if (multipleAssistancesData.active) {
      setBreadcrumbOccurrences((prev) => prev.slice(0, -1))

      setDisplayedOccurrences(() => {
        const lastBreadcrumbChildren = last(breadcrumbOccurrences.slice(0, -1))
          ?.children

        const initialMultipleAssistancesOccurrences =
          multipleAssistancesData.occurrences ?? []

        return lastBreadcrumbChildren?.length
          ? lastBreadcrumbChildren
          : initialMultipleAssistancesOccurrences
      })

      return
    }

    const prevSelectedOccurrencesChildren =
      last(prevSelectedOccurrences)?.children ?? []

    const shouldShowSalesAssociatesList =
      prevSelectedOccurrencesChildren[0]?.record.reference === 'SALES_ASSOCIATE'

    if (shouldShowSalesAssociatesList) {
      setModalContentTemplate(ModalContentTemplate.SalesAssociates)

      setBreadcrumbOccurrences((prev) => prev.slice(0, -1))

      return
    }

    setBreadcrumbOccurrences((prev) => prev.slice(0, -1))

    setDisplayedOccurrences(() => {
      return prevSelectedOccurrencesChildren.length
        ? prevSelectedOccurrencesChildren
        : options.occurrences
    })
  }

  return (
    <>
      <SalesAssociateMovementsTrackingModalHeader
        avatarUrl={salesAssociateData.avatar?.url}
        sellerName={salesAssociateData.name}
        timerDate={options.serviceStartDate}
        additionalName={
          multipleAssistancesData.active ? (
            <MultipleAssistancesCounter {...multipleAssistancesData} />
          ) : null
        }
      >
        <OccurrencesBreadcrumb occurrences={breadcrumbOccurrences} />
        <OccurrencesModalTitle
          title={options.modalTitle}
          onClick={handleBackClick}
          showGoBack={breadcrumbOccurrences.length > 0}
        />
      </SalesAssociateMovementsTrackingModalHeader>
      <SalesAssociateMovementsTrackingModalContent
        className={csx({ maxHeight: modalContentMaxHeight })}
      >
        {displayedOccurences.map((occurrence) => (
          <SalesAssociateMovementsTrackingModalItem
            key={occurrence.record.id}
            onClick={handleOccurrenceClick(occurrence)}
            hasChildren={!!occurrence.children?.length && !options.cutInLine}
            data-testid="occurrences-modal-item"
            selected={
              pendingConfirmOccurrence?.record.id === occurrence.record.id
            }
          >
            {occurrence.record.name}
          </SalesAssociateMovementsTrackingModalItem>
        ))}
      </SalesAssociateMovementsTrackingModalContent>
      <SalesAssociateMovementsTrackingModalFooter
        onCancelClick={close}
        onConfirmClick={handleOccurrencesConfirmClick}
        shouldDisableConfirmButton={!pendingConfirmOccurrence}
      />
    </>
  )
}
